import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import "@/index.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Books from "@/pages/Books";
import About from "@/pages/About";
import Speaking from "@/pages/Speaking";
import Ask from "@/pages/Ask";
import Booking from "@/pages/Booking";
import Admin from "@/pages/Admin";
import OwnerSetup from "@/pages/OwnerSetup";
import GroupCopies from "@/pages/GroupCopies";
import FreeResources from "@/pages/FreeResources";
import PdfPreview from "@/pages/PdfPreview";
import Licensing from "@/pages/Licensing";
import Facilitators from "@/pages/Facilitators";
import FacilitatorTraining from "@/pages/FacilitatorTraining";
import FacilitatorCertification from "@/pages/FacilitatorCertification";
import FacilitatorRenewal from "@/pages/FacilitatorRenewal";
import FacilitatorOrganizations from "@/pages/FacilitatorOrganizations";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-ivory flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="font-serif text-3xl text-navy">Something gave way.</p>
            <p className="mt-4 text-stone2">
              An unexpected error stopped this page from rendering. Please
              refresh — if it keeps happening, it helps to know.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const target = document.querySelector(hash);
      if (target) {
        const jump = () => {
          const top =
            target.getBoundingClientRect().top + window.scrollY - 90;
          if (window.__lenis) window.__lenis.scrollTo(top, { immediate: true });
          else window.scrollTo(0, top);
        };
        const raf = requestAnimationFrame(() =>
          requestAnimationFrame(jump)
        );
        return () => cancelAnimationFrame(raf);
      }
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function Chrome({ children }) {
  const { pathname } = useLocation();
  const quiet =
    pathname.startsWith("/admin") || pathname.startsWith("/owner-setup");
  return (
    <div className="grain min-h-screen flex flex-col">
      {!quiet && <Nav />}
      <main className="flex-1">{children}</main>
      {!quiet && <Footer />}
    </div>
  );
}

function Titled({ title, children }) {
  useEffect(() => {
    document.title = title;
  }, [title]);
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <SmoothScroll />
        <ScrollManager />
        <Chrome>
          <Routes>
            <Route
              path="/"
              element={
                <Titled title="From Diagnosis to Goodbye™">
                  <Home />
                </Titled>
              }
            />
            <Route
              path="/books"
              element={
                <Titled title="Books & Workbooks | From Diagnosis to Goodbye™">
                  <Books />
                </Titled>
              }
            />
            <Route
              path="/about"
              element={
                <Titled title="About | From Diagnosis to Goodbye™">
                  <About />
                </Titled>
              }
            />
            <Route
              path="/speaking"
              element={
                <Titled title="Speaking & Signings | From Diagnosis to Goodbye™">
                  <Speaking />
                </Titled>
              }
            />
            <Route
              path="/ask"
              element={
                <Titled title="Ask Chaplain Derrick | From Diagnosis to Goodbye™">
                  <Ask />
                </Titled>
              }
            />
            <Route
              path="/booking"
              element={
                <Titled title="Invite / Booking | From Diagnosis to Goodbye™">
                  <Booking />
                </Titled>
              }
            />
            <Route
              path="/group-copies"
              element={
                <Titled title="Group Copies | From Diagnosis to Goodbye™">
                  <GroupCopies />
                </Titled>
              }
            />
            <Route
              path="/free-resources"
              element={
                <Titled title="Free Resources | From Diagnosis to Goodbye™">
                  <FreeResources />
                </Titled>
              }
            />
            <Route
              path="/free-resources/preview/:resourceId"
              element={
                <Titled title="PDF Preview | From Diagnosis to Goodbye™">
                  <PdfPreview />
                </Titled>
              }
            />
            <Route
              path="/facilitators"
              element={
                <Titled title="Facilitator Training | From Diagnosis to Goodbye™">
                  <Facilitators />
                </Titled>
              }
            />
            <Route
              path="/facilitators/training"
              element={
                <Titled title="The Training | From Diagnosis to Goodbye™">
                  <FacilitatorTraining />
                </Titled>
              }
            />
            <Route
              path="/facilitators/certification"
              element={
                <Titled title="Certification Requirements | From Diagnosis to Goodbye™">
                  <FacilitatorCertification />
                </Titled>
              }
            />
            <Route
              path="/facilitators/renewal"
              element={
                <Titled title="Annual Renewal | From Diagnosis to Goodbye™">
                  <FacilitatorRenewal />
                </Titled>
              }
            />
            <Route
              path="/facilitators/organizations"
              element={
                <Titled title="Organizational Licensing | From Diagnosis to Goodbye™">
                  <FacilitatorOrganizations />
                </Titled>
              }
            />
            <Route path="/admin" element={<Admin />} />
            <Route path="/owner-setup" element={<OwnerSetup />} />
            <Route
              path="/licensing"
              element={
                <Titled title="Licensing & Permissions | From Diagnosis to Goodbye™">
                  <Licensing />
                </Titled>
              }
            />
            <Route
              path="*"
              element={
                <Titled title="Page not found | From Diagnosis to Goodbye™">
                  <NotFound />
                </Titled>
              }
            />
          </Routes>
        </Chrome>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
